import Globals from "./Globals.js";
//Cache
import Cache from "./core/Cache.js";
//API
import APICertEquivalency from "./operations/CertEquivalency.js";
import APICertification from "./operations/Certification.js";
import APICertRequirement from "./operations/CertRequirement.js";
import APICourse from "./operations/Course.js";
import APICourseProvider from "./operations/CourseProvider.js";
import APICustomer from "./operations/Customer.js";
import APIEquivalency from "./operations/Equivalency.js";
import APIFaq from "./operations/Faq.js";
import APIRequest from "./core/Request.js";
import APIRequirement from "./operations/Requirement.js";
import APITenant from "./operations/Tenant.js";
import APITrainingProvider from "./operations/TrainingProvider.js";
//
export default class Client {
  /* config structure
    { 
      //Cache layer
      username: 'default', endpoint: 'domain.e`xample.com', password: 'ABC@123',
      //API Layer
      endpoint: 'domain.example.com', port: 90, customerID: 'xx', tenantID: 'yy'
    }
  */
  constructor(config) {
    this.config = config;
    this._localCache = new Cache();
    //API
    this.certEquivalency = new APICertEquivalency(this);
    this.certification = new APICertification(this);
    this.certRequirement = new APICertRequirement(this);
    this.course = new APICourse(this);
    this.courseProvider = new APICourseProvider(this);
    this.customer = new APICustomer(this);
    this.equivalency = new APIEquivalency(this);
    this.requirement = new APIRequirement(this);
    this.tenant = new APITenant(this);
    this.trainingProvider = new APITrainingProvider(this);
    this.faq = new APIFaq(this);
  }
  /********* API LAYER BELOW *******/
  async newBaseRequest(method) {
    const req = new APIRequest(method);
    //Auth
    if (this.config.authorizationToken) {
      let token = this.config.authorizationToken;
      if (typeof this.config.authorizationToken === "function") token = await this.config.authorizationToken();
      req.appendHeader("Authorization", token);
    }
    // if (this.config.customerID) req.appendHeader('customerID', this.config.customerID);
    // if (this.config.tenantID) req.appendHeader('tenantID', this.config.tenantID);
    //
    req.baseURL = this.config.endpoint;
    if (this.config.port) req.baseURL += `:${this.config.port}`;
    //
    return req;
  }
  /********* CACHE LAYER BELOW *******/
  /* Customers */
  async getAllCustomers() {
    let allCustomers = await this._localCache.getValue(Globals.RemoteCacheCustomerPrefix);
    if (!allCustomers) {
      //secondary attempt goes to cache
      console.debug("ConfigClient - Customers Fetch All");
      allCustomers = await this._getAllRemoteCustomers();
      if (!allCustomers) return null;
      //Parse
      allCustomers = Array.isArray(allCustomers) ? allCustomers.map((c) => JSON.parse(c)) : allCustomers;
      //Save on ram cache
      await this._localCache.setValue(Globals.RemoteCacheCustomerPrefix, allCustomers, Globals.CacheAllCustomerTTL);
    }
    return allCustomers;
  }
  async getCustomerByID(customerID) {
    let customer = await this._localCache.getValue(`${Globals.RemoteCacheCustomerPrefix}${customerID}`);
    //Attempt find all as secondary
    if (!customer) {
      const allCustomers = await this._localCache.getValue(Globals.RemoteCacheCustomerPrefix);
      customer = allCustomers ? allCustomers.find((c) => c.id == customerID) : null;
    }
    //Third attempt, goes to cache
    if (!customer) {
      console.debug("ConfigClient - Customer Fetch:", customerID);
      await this._initCacheClient();
      customer = await this._remoteCache.get(`${Globals.RemoteCacheCustomerPrefix}${customerID}`);
      if (!customer) return null;
      //Parse
      customer = JSON.parse(customer);
      //Save on ram cache
      await this._localCache.setValue(`${Globals.RemoteCacheCustomerPrefix}${customerID}`, customer, Globals.CacheCustomerTTL);
    }
    return customer;
  }

  /* Customer Course provider */
  async getCourseProviderByIDs(courseProviderID, customerID) {
    const customer = await this.getCustomerByID(customerID);
    //For all providers
    for (let provider of customer.courseProviders) {
      if (provider == courseProviderID) return provider;
    }
    return null;
  }

  /* Tenants */
  async getAllTenants() {
    let allTenants = await this._localCache.getValue(Globals.RemoteCacheTenantPrefix);
    if (!allTenants) {
      //secondary attempt goes to cache
      console.debug("ConfigClient - Tenants Fetch All");
      allTenants = await this._getAllRemoteTenants();
      if (!allTenants) return null;
      //Parse
      allTenants = Array.isArray(allTenants) ? allTenants.map((t) => JSON.parse(t)) : allTenants;
      //Save on ram cache
      await this._localCache.setValue(Globals.RemoteCacheTenantPrefix, allTenants, Globals.CacheAllTenantsTTL);
    }
    return allTenants;
  }
  async getTenantByID(tenantID) {
    let tenant = await this._localCache.getValue(`${Globals.RemoteCacheTenantPrefix}${tenantID}`);
    //Attempt find all as secondary
    // if (!tenant) {
    //   const allTenants = await this._localCache.getValue(Globals.RemoteCacheTenantPrefix);
    //   tenant = (allTenants ? allTenants.find((t) => t.id == tenantID) : null);
    // }
    //Third attempt, goes to cache
    if (!tenant) {
      console.debug("ConfigClient - Tenant Fetch:", tenantID);
      await this._initCacheClient();
      console.log("done");
      tenant = await this._remoteCache.get(`${Globals.RemoteCacheTenantPrefix}${tenantID}`);
      if (!tenant) return null;
      //Parse
      tenant = JSON.parse(tenant);
      //Save on ram cache
      await this._localCache.setValue(`${Globals.RemoteCacheTenantPrefix}${tenantID}`, tenant, Globals.CacheTenantTTL);
    }
    return tenant;
  }

  /* Tenant Certification & Courses */
  async getCertificationByID(certID, tenantID) {
    const tenant = await this.getTenantByID(tenantID);
    if (tenant) for (let certificationObj of tenant.certifications) if (certificationObj.id == certID) return certificationObj;
    return null;
  }
  async getCourseByID(courseID, tenantID) {
    const tenant = await this.getTenantByID(tenantID);
    if (!tenant) return null;
    //For all certifications
    for (let certificationObj of tenant.certifications) {
      //For all courses of tenant config
      for (let course of certificationObj.requirements) {
        if (Array.isArray(course)) {
          for (let subCourse of course) if (subCourse.id == courseID) return subCourse;
        } else if (course.id == courseID) return course;
      }
    }
    return null;
  }
  async getCourseInCertificationByID(certificationID, courseID, tenantID) {
    const tenant = await this.getTenantByID(tenantID);
    if (!tenant) return null;
    //Find the certification in tenant
    console.log(`search tenant for ${certificationID}`, tenant.certifications);
    const certificationObj = tenant.certifications.find((c) => c.id == certificationID);
    if (!certificationObj) return null;
    //For all courses of certificatiojm
    for (let course of certificationObj.requirements) {
      if (Array.isArray(course)) {
        for (let subCourse of course) if (subCourse.id == courseID) return subCourse;
      } else if (course.id == courseID) return course;
    }
    return null;
  }
  async getAllCoursesByTenantID(tenantID) {
    const tenant = await this.getTenantByID(tenantID);
    if (!tenant) return null;
    const courses = [];
    //For all certifications
    for (const certificationObj of tenant.certifications) {
      //For all courses of tenant config
      for (const course of certificationObj.requirements) {
        if (Array.isArray(course)) {
          for (const subCourse of course) courses.push(subCourse);
        } else courses.push(course);
      }
    }
    return courses;
  }
  async getAllCoursesByCertificationID(certID, tenantID) {
    const tenant = await this.getTenantByID(tenantID);
    if (!tenant) return null;
    const courses = [];
    //For all certifications
    for (const certificationObj of tenant.certifications) {
      if (certificationObj.id != certID) continue;
      //For all courses of tenant config
      for (const course of certificationObj.requirements) {
        if (Array.isArray(course)) {
          for (const subCourse of course) courses.push(subCourse);
        } else courses.push(course);
      }
    }
    return courses;
  }
  async getAllCourses() {
    return await this.getAllCoursesByCustomerID(null);
  }
  async getAllCoursesByCustomerID(customerID) {
    const courses = [];
    //better to get all tenants and filter locally
    const allTenants = (await this.getAllTenants()).filter((t) => !customerID || t.customerID == customerID);
    for (const tenant of allTenants || []) {
      //For all certifications
      for (const certificationObj of tenant.certifications) {
        //For all courses of tenant config
        for (const course of certificationObj.requirements) {
          if (Array.isArray(course)) {
            for (const subCourse of course) {
              const found = courses.find((obj) => obj.id == subCourse.id);
              if (!found) courses.push(subCourse);
            }
          } else {
            const found = courses.find((obj) => obj.id == course.id);
            if (!found) courses.push(course);
          }
        }
      }
    }
    return courses;
  }

  /* private */
  async _getAllRemoteCustomers() {
    await this._initCacheClient();
    const allKeys = await this._remoteCache.keys(`${Globals.RemoteCacheCustomerPrefix}*`);
    return await this._remoteCache.mget(allKeys || []);
  }
  async _getAllRemoteTenants() {
    await this._initCacheClient();
    const allKeys = await this._remoteCache.keys(`${Globals.RemoteCacheTenantPrefix}*`);
    return await this._remoteCache.mget(allKeys || []);
  }
  async _initCacheClient() {
    //check if using cache layer
    if (!this._remoteCache && this.config.username && this.config.version == 5) {
      // dynamic import due to those deps not being deps of this module
      // and being allowed to be imported by consumers when a certain
      // feature of this module (caching) is enabled/used
      const dynamicImport = ["@ikonintegration/ikapi"];
      const { Redis } = await import(dynamicImport[0]);
      this._remoteCache = await Redis.connection({
        hostname: this.config.endpoint,
        username: this.config.username,
        password: this.config.password,
        enableTLS: true,
      });
    } else if (!this._remoteCache && this.config.username) {
      // dynamic import due to those deps not being deps of this module
      // and being allowed to be imported by consumers when a certain
      // feature of this module (caching) is enabled/used
      const dynamicImport = ["redis", "@ikonintegration/ikapi"];
      await import(dynamicImport[0]);
      const IKAPI = (await import(dynamicImport[1])).default;
      this._remoteCache = new IKAPI.IKRedis({
        host: this.config.endpoint,
        user: this.config.username,
        password: this.config.password,
        enableTLS: true,
      });
    }
  }
}
